// moodSave.js — ONE mood per local day, saved once (2026-10-15).
//
// The bug: nine taps on "Log Mood" within five seconds wrote nine rows. The
// save was read-then-write (find today's row, update it or insert) with no
// in-flight guard and no button feedback, so nine concurrent saves each saw
// no row and each inserted. Policy, decided 2026-10-15: one mood per local
// day; "Edit" replaces today's row; repeat taps while a save is in flight
// join that save instead of starting another; after an insert the day is
// re-read and any extra rows (a race that slipped through) are removed,
// keeping the newest.
//
// Pure, injectable, unit tested. The client wires Supabase into
// createMoodSaver; dates here are DEVICE-local (the user's own clock), the
// same calendar the mood card and the habit day use.

/** Device-local 'YYYY-MM-DD' for an instant (ISO string, ms, or Date). */
export function moodLocalDate(v) {
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** ISO bounds of the device-local calendar day containing `now`. */
export function localDayBoundsIso(now = new Date()) {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

/**
 * @param {object} io
 * @param {() => Promise<Array<{id:string, note?:string|null}>>} io.findToday  today's rows, NEWEST first
 * @param {(row:{score:number, note:string|null}) => Promise<boolean>} io.insert
 * @param {(id:string, patch:{score:number, note:string|null}) => Promise<boolean>} io.update
 * @param {(ids:string[]) => Promise<boolean>} [io.remove]  delete extra same-day rows
 * @returns {(score:number, note?:string|null) => Promise<{ok:boolean, mode:'inserted'|'updated'|'noop', existing:object|null}>}
 */
export function createMoodSaver({ findToday, insert, update, remove }) {
  let inFlight = null;
  return function save(score, note) {
    // A tap while a save is in flight joins that save: one write, one result.
    if (inFlight) return inFlight;
    inFlight = (async () => {
      const rows = (await findToday()) || [];
      const existing = rows[0] || null;
      const trimmed = typeof note === 'string' ? note.trim() : '';
      let ok, mode;
      if (existing) {
        // An edit PRESERVES the saved note unless the user typed a new one:
        // the one-tap sheet and a reopened grid send an empty note, and that
        // must not erase what they wrote earlier today.
        const patch = { score, note: trimmed ? note : (existing.note ?? null) };
        ok = await update(existing.id, patch);
        mode = 'updated';
        if (ok && rows.length > 1 && remove) await remove(rows.slice(1).map(r => r.id)).catch?.(() => false);
      } else {
        ok = await insert({ score, note: trimmed ? note : null });
        mode = 'inserted';
        if (ok && remove) {
          // Self-heal a race that slipped past the guard (two devices, two
          // tabs): keep the newest row for the day, drop the rest.
          const after = (await findToday()) || [];
          if (after.length > 1) await remove(after.slice(1).map(r => r.id));
        }
      }
      return { ok: !!ok, mode, existing };
    })().finally(() => { inFlight = null; });
    return inFlight;
  };
}
